/**
 * Local Bridge MarketFit: define a situation's assets, needs, inputs, outputs, and actors (with primary).
 * Uses SituationDefinitionForm for the form content.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Drawer, Card, Button, Space } from 'antd';
import { BulbOutlined, CloseOutlined } from '@ant-design/icons';
import {
  getMarketFitSituation,
  updateMarketFitSituation,
  listMarketFitActors,
  createMarketFitActor,
  type MarketFitActor,
} from '../../services/localBridgeSessionApi';
import {
  parseSituationDetails,
  parseSituationSchemaV2,
  getMarketFitSchemaVersion,
  defaultSituationSchemaV2,
  type SituationDetails,
  type SituationSchemaV2,
} from '../../types/marketFitLocalBridge';
import { SituationDefinitionForm } from '../MarketFit/SituationDefinitionForm';
import { message } from 'antd';

interface SituationDefinitionDrawerProps {
  open: boolean;
  situationId: string | null;
  companyId: string;
  onClose: () => void;
  onSaved: () => void;
  variant?: 'drawer' | 'panel';
}

export const SituationDefinitionDrawer: React.FC<SituationDefinitionDrawerProps> = ({
  open,
  situationId,
  companyId,
  onClose,
  onSaved,
  variant = 'drawer',
}) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [situationName, setSituationName] = useState('');
  const [data, setData] = useState<SituationDetails>({
    assets: [],
    needs: [],
    inputs: [],
    outputs: [],
    actors: [],
  });
  const [actors, setActors] = useState<MarketFitActor[]>([]);
  const [schemaMode, setSchemaMode] = useState<'legacy' | 'v2'>('legacy');
  const [dataV2, setDataV2] = useState<SituationSchemaV2>(defaultSituationSchemaV2());

  const loadSituationAndActors = useCallback(async () => {
    if (!situationId) return;
    if (variant === 'drawer' && !open) return;
    setLoading(true);
    try {
      const [s, actorList] = await Promise.all([
        getMarketFitSituation(situationId),
        listMarketFitActors(companyId),
      ]);
      if (s) {
        setSituationName(s.name);
        const detailsJson = s.details_json ?? '';
        setData(parseSituationDetails(detailsJson));
        setDataV2(parseSituationSchemaV2(detailsJson));
        setSchemaMode(getMarketFitSchemaVersion(detailsJson) === 2 ? 'v2' : 'legacy');
      } else {
        setSituationName('');
        setData({ assets: [], needs: [], inputs: [], outputs: [], actors: [] });
        setDataV2(defaultSituationSchemaV2());
        setSchemaMode('legacy');
      }
      setActors(actorList);
    } finally {
      setLoading(false);
    }
  }, [open, situationId, companyId, variant]);

  useEffect(() => {
    loadSituationAndActors();
  }, [loadSituationAndActors]);

  const handleSave = async () => {
    if (!situationId) return;
    const details_json =
      schemaMode === 'v2'
        ? JSON.stringify(dataV2)
        : JSON.stringify({
            assets: data.assets.filter((a) => a.name?.trim()),
            needs: data.needs.filter((n) => n.name?.trim()),
            inputs: data.inputs.filter((i) => i.name?.trim()),
            outputs: data.outputs.filter((o) => o.name?.trim()),
            actors: data.actors,
          });
    setSaving(true);
    try {
      const updated = await updateMarketFitSituation(situationId, { details_json });
      if (updated) {
        message.success('Situation definition saved');
        onSaved();
      } else {
        message.error('Failed to save');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleCreateActor = useCallback(
    async (name: string) => {
      const created = await createMarketFitActor({ company_id: companyId, actor_name: name });
      if (created) {
        setActors((prev) => [...prev, created]);
        setData((prev) => ({
          ...prev,
          actors: [{ actor_id: created.id, is_primary: true }],
        }));
        message.success('Actor created');
      } else {
        message.error('Failed to create actor');
      }
    },
    [companyId]
  );

  const footer = (
    <Space>
      <Button onClick={onClose} icon={variant === 'panel' ? <CloseOutlined /> : undefined}>
        Close
      </Button>
      <Button type="primary" loading={saving} onClick={handleSave}>
        Save
      </Button>
    </Space>
  );

  const formContent = (
    <SituationDefinitionForm
      data={data}
      dataV2={dataV2}
      schemaMode={schemaMode}
      onDataChange={setData}
      onDataV2Change={setDataV2}
      onSchemaModeChange={setSchemaMode}
      onSave={handleSave}
      loading={loading}
      saving={saving}
      actors={actors}
      companyId={companyId}
      situationId={situationId}
      onCreateActor={handleCreateActor}
      showFooter={false}
    />
  );

  if (variant === 'panel') {
    return (
      <Card
        title={
          <Space>
            <BulbOutlined />
            <span>Define situation: {situationName || '…'}</span>
          </Space>
        }
        extra={<Button type="text" icon={<CloseOutlined />} onClick={onClose} />}
        style={{ height: '100%' }}
      >
        {formContent}
        <div style={{ marginTop: 16 }}>{footer}</div>
      </Card>
    );
  }
  return (
    <Drawer
      title={
        <Space>
          <BulbOutlined />
          <span>Define situation: {situationName || '…'}</span>
        </Space>
      }
      open={open}
      onClose={onClose}
      width={680}
      footer={footer}
    >
      {formContent}
    </Drawer>
  );
};

export default SituationDefinitionDrawer;
